import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api';
import { Plus, FileText, Send, Trash2, Download, Check, Loader2, X, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const JustRenovated = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    property_address: '',
    bill_to: '',
    project_description: '',
    line_items: [{ description: '', quantity: 1, unit_price: 0 }],
    vat_applicable: false,
    vat_rate: 20,
    payment_terms: 14,
    notes: '',
    deposit_paid: ''
  });

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/jr/invoices`);
      const data = await res.json();
      setInvoices(data.invoices || []);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLineItemChange = (index, field, value) => {
    const newItems = [...formData.line_items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData(prev => ({ ...prev, line_items: newItems }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, unit_price: 0 }]
    }));
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length > 1) {
      setFormData(prev => ({
        ...prev,
        line_items: prev.line_items.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateTotal = () => {
    const subtotal = formData.line_items.reduce((sum, item) => 
      sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), 0);
    const vat = formData.vat_applicable ? subtotal * (formData.vat_rate / 100) : 0;
    const total = subtotal + vat;
    const deposit = parseFloat(formData.deposit_paid) || 0;
    const balance = total - deposit;
    return { subtotal, vat, total, deposit, balance };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/jr/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          line_items: formData.line_items.map(item => ({
            ...item,
            quantity: parseFloat(item.quantity) || 1,
            unit_price: parseFloat(item.unit_price) || 0
          })),
          payment_terms: parseInt(formData.payment_terms) || 14,
          deposit_paid: parseFloat(formData.deposit_paid) || 0
        })
      });
      if (res.ok) {
        setShowForm(false);
        setFormData({
          client_name: '',
          client_email: '',
          client_address: '',
          invoice_addressed_to: '',
          project_description: '',
          line_items: [{ description: '', quantity: 1, unit_price: 0 }],
          vat_applicable: false,
          vat_rate: 20,
          payment_terms: 14,
          notes: '',
          deposit_paid: ''
        });
        fetchInvoices();
      }
    } catch (err) {
      console.error('Failed to create invoice:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    setSendingId(invoiceId);
    try {
      const res = await fetch(`${API_URL}/api/admin/jr/invoices/${invoiceId}/send`, { method: 'POST' });
      if (res.ok) {
        fetchInvoices();
      }
    } catch (err) {
      console.error('Failed to send invoice:', err);
    } finally {
      setSendingId(null);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await fetch(`${API_URL}/api/admin/jr/invoices/${invoiceId}`, { method: 'DELETE' });
      fetchInvoices();
      if (selectedInvoice?.id === invoiceId) setSelectedInvoice(null);
    } catch (err) {
      console.error('Failed to delete invoice:', err);
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await fetch(`${API_URL}/api/admin/jr/invoices/${invoiceId}/status?status=paid`, { method: 'PUT' });
      fetchInvoices();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-600',
      sent: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      overdue: 'bg-red-100 text-red-700'
    };
    return styles[status] || styles.draft;
  };

  const totals = calculateTotal();

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </span>
            Just Renovated
          </h1>
          <p className="text-gray-500 text-sm mt-1">Renovation invoicing system</p>
        </div>
        <Button 
          onClick={() => setShowForm(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="create-jr-invoice-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Invoice
        </Button>
      </div>

      {/* Create Invoice Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Create Invoice</h2>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Client Details */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 text-sm uppercase tracking-wide">Client Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 mb-1.5 block">Client Name *</Label>
                    <Input
                      name="client_name"
                      value={formData.client_name}
                      onChange={handleInputChange}
                      required
                      className="h-10"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 mb-1.5 block">Client Email *</Label>
                    <Input
                      name="client_email"
                      type="email"
                      value={formData.client_email}
                      onChange={handleInputChange}
                      required
                      className="h-10"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-700 mb-1.5 block">Property Address * <span className="text-gray-400 font-normal">(where works were carried out)</span></Label>
                  <textarea
                    name="property_address"
                    value={formData.property_address}
                    onChange={handleInputChange}
                    required
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-orange-500 outline-none resize-none"
                    placeholder="123 High Street, London, SW1A 1AA"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 mb-1.5 block">Bill To <span className="text-gray-400 font-normal">(manual entry - who to address invoice to)</span></Label>
                  <textarea
                    name="bill_to"
                    value={formData.bill_to}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-orange-500 outline-none resize-none"
                    placeholder="FAO: Accounts Department&#10;ABC Property Management Ltd&#10;456 Business Park, London EC1A 1BB"
                  />
                </div>
              </div>

              {/* Project Description */}
              <div>
                <Label className="text-gray-700 mb-1.5 block">Project Description <span className="text-gray-400 font-normal">(optional)</span></Label>
                <textarea
                  name="project_description"
                  value={formData.project_description}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-orange-500 outline-none resize-none"
                  placeholder="Kitchen renovation"
                />
              </div>

              {/* Line Items */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-900 text-sm uppercase tracking-wide">Line Items</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                </div>
                
                {formData.line_items.map((item, index) => (
                  <div key={index} className="flex gap-3 items-start bg-gray-50 p-3 rounded-lg">
                    <div className="flex-1">
                      <Input
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                        placeholder="Description"
                        className="h-9"
                      />
                    </div>
                    <div className="w-20">
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                        placeholder="Qty"
                        className="h-9"
                        min="1"
                        step="0.01"
                      />
                    </div>
                    <div className="w-28">
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                        <Input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                          placeholder="Price"
                          className="h-9 pl-6"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                    <div className="w-24 text-right pt-2 font-medium text-gray-700">
                      £{((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toFixed(2)}
                    </div>
                    {formData.line_items.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeLineItem(index)}
                        className="p-2 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* VAT & Payment Terms */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="vat_applicable"
                    name="vat_applicable"
                    checked={formData.vat_applicable}
                    onChange={handleInputChange}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <Label htmlFor="vat_applicable" className="text-gray-700 cursor-pointer">
                    VAT Applicable ({formData.vat_rate}%)
                  </Label>
                </div>
                <div>
                  <Label className="text-gray-700 mb-1.5 block">Payment Terms (days)</Label>
                  <Input
                    name="payment_terms"
                    type="number"
                    value={formData.payment_terms}
                    onChange={handleInputChange}
                    className="h-10"
                    min="1"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-gray-700 mb-1.5 block">Notes (optional)</Label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-orange-500 outline-none resize-none"
                  placeholder="Additional notes for the invoice..."
                />
              </div>

              {/* Deposit Already Paid */}
              <div>
                <Label className="text-gray-700 mb-1.5 block">Deposit Already Paid (optional)</Label>
                <p className="text-xs text-gray-500 mb-2">If the client has paid a deposit, enter the amount here. It will be deducted from the total.</p>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">£</span>
                  <Input
                    type="number"
                    name="deposit_paid"
                    value={formData.deposit_paid}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    className="w-32"
                    placeholder="0.00"
                    data-testid="jr-deposit-paid-input"
                  />
                </div>
              </div>

              {/* Totals */}
              <div className="bg-orange-50 rounded-xl p-4">
                <div className="flex justify-between text-gray-600 mb-2">
                  <span>Subtotal:</span>
                  <span>£{totals.subtotal.toFixed(2)}</span>
                </div>
                {formData.vat_applicable ? (
                  <div className="flex justify-between text-gray-600 mb-2">
                    <span>VAT ({formData.vat_rate}%):</span>
                    <span>£{totals.vat.toFixed(2)}</span>
                  </div>
                ) : (
                  <div className="flex justify-between text-gray-600 mb-2">
                    <span>VAT:</span>
                    <span className="text-sm">Not applicable (not VAT registered)</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-semibold text-gray-700 pt-2 border-t border-orange-200">
                  <span>Invoice Total:</span>
                  <span>£{totals.total.toFixed(2)}</span>
                </div>
                {totals.deposit > 0 && (
                  <>
                    <div className="flex justify-between text-green-600 mt-2">
                      <span>Less: Deposit Paid:</span>
                      <span>-£{totals.deposit.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-semibold text-orange-600 pt-2 border-t border-orange-200 mt-2">
                      <span>Balance Due:</span>
                      <span>£{totals.balance.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Invoice
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h2 className="text-lg font-semibold text-white">{selectedInvoice.invoice_number}</h2>
                <p className="text-orange-100 text-sm">{formatDate(selectedInvoice.invoice_date)}</p>
              </div>
              <button onClick={() => setSelectedInvoice(null)} className="p-2 hover:bg-white/20 rounded-lg text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Status and Actions */}
              <div className="flex items-center justify-between">
                <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusBadge(selectedInvoice.status)}`}>
                  {selectedInvoice.status}
                </span>
                <div className="flex gap-2">
                  <a
                    href={`${API_URL}/api/admin/jr/invoices/${selectedInvoice.id}/download-pdf`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <Download className="w-4 h-4" /> PDF
                  </a>
                  {selectedInvoice.status !== 'paid' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => handleSendInvoice(selectedInvoice.id)}
                        disabled={sendingId === selectedInvoice.id}
                        className="bg-orange-500 hover:bg-orange-600"
                      >
                        {sendingId === selectedInvoice.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        {selectedInvoice.status === 'sent' ? 'Resend' : 'Send'}
                      </Button>
                      {(selectedInvoice.status === 'sent' || selectedInvoice.status === 'overdue') && (
                        <Button size="sm" onClick={() => handleMarkPaid(selectedInvoice.id)} className="bg-green-600 hover:bg-green-700">
                          <Check className="w-4 h-4 mr-1" /> Mark Paid
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Client Info */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">BILL TO</h3>
                {selectedInvoice.bill_to ? (
                  <p className="text-gray-900 whitespace-pre-line">{selectedInvoice.bill_to}</p>
                ) : selectedInvoice.invoice_addressed_to ? (
                  <>
                    <p className="font-semibold text-gray-900">{selectedInvoice.invoice_addressed_to}</p>
                    <p className="text-gray-900">{selectedInvoice.client_name}</p>
                  </>
                ) : (
                  <p className="text-gray-900">{selectedInvoice.client_name}</p>
                )}
              </div>

              {/* Property Address */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">PROPERTY ADDRESS</h3>
                <p className="text-gray-900 whitespace-pre-line">{selectedInvoice.property_address || selectedInvoice.client_address || 'N/A'}</p>
                <p className="text-gray-600 text-sm mt-1">{selectedInvoice.client_email}</p>
              </div>

              {/* Project Description */}
              {selectedInvoice.project_description && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">PROJECT</h3>
                  <p className="text-gray-900">{selectedInvoice.project_description}</p>
                </div>
              )}

              {/* Line Items */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">ITEMS</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-2 text-sm font-medium text-gray-600">Description</th>
                        <th className="text-right px-4 py-2 text-sm font-medium text-gray-600">Qty</th>
                        <th className="text-right px-4 py-2 text-sm font-medium text-gray-600">Price</th>
                        <th className="text-right px-4 py-2 text-sm font-medium text-gray-600">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedInvoice.line_items || []).map((item, idx) => (
                        <tr key={idx} className="border-t border-gray-100">
                          <td className="px-4 py-2 text-gray-900">{item.description}</td>
                          <td className="px-4 py-2 text-gray-600 text-right">{item.quantity}</td>
                          <td className="px-4 py-2 text-gray-600 text-right">£{(item.unit_price || 0).toFixed(2)}</td>
                          <td className="px-4 py-2 text-gray-900 text-right font-medium">£{(item.total || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between text-gray-600 mb-2">
                  <span>Subtotal:</span>
                  <span>£{(selectedInvoice.subtotal || 0).toFixed(2)}</span>
                </div>
                {selectedInvoice.vat_applicable ? (
                  <div className="flex justify-between text-gray-600 mb-2">
                    <span>VAT ({selectedInvoice.vat_rate}%):</span>
                    <span>£{(selectedInvoice.vat_amount || 0).toFixed(2)}</span>
                  </div>
                ) : (
                  <div className="flex justify-between text-gray-600 mb-2">
                    <span>VAT:</span>
                    <span className="text-sm">Not applicable (not VAT registered)</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-semibold text-gray-700 pt-2 border-t border-gray-200">
                  <span>Invoice Total:</span>
                  <span>£{(selectedInvoice.total_amount || 0).toFixed(2)}</span>
                </div>
                {selectedInvoice.deposit_paid > 0 && (
                  <>
                    <div className="flex justify-between text-green-600 mt-2">
                      <span>Less: Deposit Received:</span>
                      <span>-£{(selectedInvoice.deposit_paid || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-semibold text-orange-600 pt-2 border-t border-gray-200 mt-2">
                      <span>Balance Due:</span>
                      <span>£{(selectedInvoice.balance_due || 0).toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Payment Info */}
              <div className="bg-orange-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-orange-800 mb-2">Payment Details</h3>
                <p className="text-gray-700">Due: {formatDate(selectedInvoice.due_date)}</p>
                <p className="text-gray-600 text-sm mt-2">
                  Bank: Just Renovated Ltd | Account: 53225659 | Sort: 23-05-80
                </p>
              </div>

              {/* Notes */}
              {selectedInvoice.notes && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">NOTES</h3>
                  <p className="text-gray-600">{selectedInvoice.notes}</p>
                </div>
              )}

              {/* Delete */}
              <div className="pt-4 border-t">
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => handleDeleteInvoice(selectedInvoice.id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete Invoice
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoices List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-orange-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Invoices Yet</h3>
          <p className="text-gray-500 mb-6">Create your first Just Renovated invoice</p>
          <Button onClick={() => setShowForm(true)} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="w-4 h-4 mr-2" /> Create Invoice
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-600">Invoice</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-600">Property</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-600">Date</th>
                <th className="text-right px-6 py-4 text-sm font-medium text-gray-600">Amount</th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-600">Status</th>
                <th className="text-right px-6 py-4 text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <button 
                      onClick={() => setSelectedInvoice(invoice)}
                      className="font-medium text-orange-600 hover:text-orange-700"
                    >
                      {invoice.invoice_number}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-gray-900 truncate max-w-[200px]" title={invoice.property_address || invoice.client_address || invoice.client_name}>
                      {(invoice.property_address || invoice.client_address || '').split('\n')[0] || invoice.client_name}
                    </div>
                    <div className="text-xs text-gray-500 truncate max-w-[200px]">{invoice.client_name}</div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{formatDate(invoice.invoice_date)}</td>
                  <td className="px-6 py-4 text-right font-medium text-gray-900">£{(invoice.total_amount || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setSelectedInvoice(invoice)}
                        className="p-2 text-gray-400 hover:text-gray-600"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <a
                        href={`${API_URL}/api/admin/jr/invoices/${invoice.id}/download-pdf`}
                        className="p-2 text-gray-400 hover:text-gray-600"
                        title="Download PDF"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      {invoice.status !== 'paid' && (
                        <button
                          onClick={() => handleSendInvoice(invoice.id)}
                          disabled={sendingId === invoice.id}
                          className="p-2 text-gray-400 hover:text-orange-600"
                          title="Send Invoice"
                        >
                          {sendingId === invoice.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                        <button
                          onClick={() => handleMarkPaid(invoice.id)}
                          className="p-2 text-gray-400 hover:text-green-600"
                          title="Mark as Paid"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteInvoice(invoice.id)}
                        className="p-2 text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default JustRenovated;
